import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import styles from "./CluesStrip.module.scss";
import { ChevronLeft, ChevronRight, Image as ImageIcon, KeyRound } from "lucide-react";

import { PATH } from '../constants';

/**
 * CluesStrip
 * Affiche les indices (image ou clé texte) issus des questions répondues (achieved=true)
 * stockées dans localStorage sous la clé `questions_inde_${gameInfoId}`.
 *
 * Props:
 * - gameInfoId: (string|number) obligatoire → pour construire la clé localStorage
 * - title?: string → titre au-dessus du carrousel
 * - emptyLabel?: string → message si aucun indice
 * - className?: string → classe wrapper optionnelle
 * - storageKeyOverride?: string → si tu veux passer une clé custom au lieu du format par défaut
 */
const CluesStrip = ({
  gameInfoId,
  title = "Indices trouvés",
  emptyLabel = "Aucun indice pour le moment.",
  className = "",
  storageKeyOverride,
}) => {
  const [clues, setClues] = useState([]);
  const [hasOverflow, setHasOverflow] = useState(false);
  const trackRef = useRef(null);

  const storageKey = useMemo(
    () => storageKeyOverride || `questions_inde_${gameInfoId}`,
    [storageKeyOverride, gameInfoId]
  );

  const computeCluesFromStorage = useCallback(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return [];
      const questions = JSON.parse(raw);
      if (!Array.isArray(questions)) return [];

      // Filtre: achieved === true et au moins un indice présent
      const items = questions
        .filter(q => q && q.achieved === true && (q.img_indice || q.cle_indice))
        .map(q => ({
          id: q.id ?? String(Math.random()),
          img: q.img_indice || null,
          text: q.cle_indice || null,
        }));

      return items;
    } catch (e) {
      console.warn("[CluesStrip] Impossible de lire/parcer le localStorage", e);
      return [];
    }
  }, [storageKey]);

  // Chargement initial
  useEffect(() => {
    setClues(computeCluesFromStorage());
  }, [computeCluesFromStorage]);

  // Écoute les changements (ex: autre onglet, ou mise à jour dans l’app)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === storageKey) {
        setClues(computeCluesFromStorage());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storageKey, computeCluesFromStorage]);

  // API publique minimale: méthode pour forcer un refresh depuis parent (optionnel)
  // -> usage: <CluesStrip ref={ref}/> et ref.current?.refresh()
  const apiRef = useRef({ refresh: () => setClues(computeCluesFromStorage()) });
  useEffect(() => {
    apiRef.current.refresh = () => setClues(computeCluesFromStorage());
  }, [computeCluesFromStorage]);

  // Détection overflow pour afficher/masquer les flèches
  const checkOverflow = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setHasOverflow(el.scrollWidth > el.clientWidth + 4);
  }, []);
  useEffect(() => {
    checkOverflow();
    const r = new ResizeObserver(checkOverflow);
    if (trackRef.current) r.observe(trackRef.current);
    return () => r.disconnect();
  }, [clues, checkOverflow]);

  // Scroll contrôlé
  const scrollByAmount = (dir = 1) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector(`.${styles.card}`);
    const step = card ? card.clientWidth + 16 : 240; // 1 carte + gap approx.
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  if (!gameInfoId) {
    return null; // sécurité : il faut un id pour lire le LS
  }

  return (
    <section className={`${styles.wrapper} ${className}`}>
      <div className={styles.header}>
        <h3 className={styles.title}>{title}</h3>
        {hasOverflow && (
          <div className={styles.arrows}>
            <button
              type="button"
              className={styles.arrowBtn}
              aria-label="Précédent"
              onClick={() => scrollByAmount(-1)}
            >
              <ChevronLeft />
            </button>
            <button
              type="button"
              className={styles.arrowBtn}
              aria-label="Suivant"
              onClick={() => scrollByAmount(1)}
            >
              <ChevronRight />
            </button>
          </div>
        )}
      </div>

      {clues.length === 0 ? (
        <p className={styles.empty}>{emptyLabel}</p>
      ) : (
        <div className={styles.trackWrapper}>
          <div className={styles.track} ref={trackRef}>
            {clues.map((c) => (
              <article key={c.id} className={styles.card}>
                {c.img ? (
                  <div className={styles.imageBox} title="Indice image">
                    {/* si l’API renvoie un chemin relatif, adapter ici */}
                    <img
                      src={String(c.img).startsWith("http") ? c.img : `${PATH}${c.img}`}
                      alt="Indice visuel"
                      className={styles.image}
                      loading="lazy"
                    />
                    <div className={styles.badge}><ImageIcon size={16} /> Image</div>
                  </div>
                ) : (
                  <div className={styles.textBox} title="Indice texte">
                    <div className={styles.textIcon}><KeyRound size={18} /></div>
                    <p className={styles.text}>{c.text}</p>
                    {/* <div className={styles.badge}><KeyRound size={16} /> Clé</div> */}
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default CluesStrip;
